import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Check,
  Copy,
  Link2,
  Loader2,
  RefreshCw,
  Share2,
  X,
} from "lucide-react";
import Modal from "components/common/Modal";
import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import { showToast } from "utils/toast";
import ConfirmDialog from "../common/ConfirmDialog";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
};

const primaryButton =
  "inline-flex items-center justify-center gap-2 rounded-[10px] bg-gradient-to-br from-[#E8854A] to-[#D66B2E] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(224,122,58,0.3)] transition-all duration-150 hover:shadow-[0_4px_12px_rgba(224,122,58,0.4)] disabled:cursor-not-allowed disabled:opacity-60";
const secondaryButton =
  "inline-flex items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-60";

/**
 * The team's shareable invite link. One link per team: the coach creates it,
 * copies or shares it (e.g. into the parents' group chat), and can replace or
 * revoke it. Opening it goes to /v2/join/:token.
 */
const TeamInviteLinkModal = ({ show, onClose, workspaceId, team }) => {
  const { t } = useTranslation();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null); // "regenerate" | "revoke"

  const teamId = team?.id;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await betaAPI.getTeamInviteLink({ workspaceId, teamId });
      setLink(data?.invite_link || null);
    } catch (err) {
      console.error("Failed to load team invite link", err);
      showToast.error(t("team.inviteLink.toasts.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, teamId, t]);

  useEffect(() => {
    if (show && workspaceId && teamId) load();
    if (!show) {
      setCopied(false);
      setConfirm(null);
    }
  }, [show, workspaceId, teamId, load]);

  const create = async () => {
    setConfirm(null);
    setBusy(true);
    try {
      const { data } = await betaAPI.createTeamInviteLink({
        workspaceId,
        teamId,
      });
      setLink(data.invite_link);
      setCopied(false);
      showToast.success(
        link
          ? t("team.inviteLink.toasts.regenerated")
          : t("team.inviteLink.toasts.created")
      );
    } catch (err) {
      console.error("Failed to create team invite link", err);
      showToast.error(t("team.inviteLink.toasts.createFailed"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setConfirm(null);
    setBusy(true);
    try {
      await betaAPI.revokeTeamInviteLink({ workspaceId, teamId });
      setLink(null);
      showToast.success(t("team.inviteLink.toasts.revoked"));
    } catch (err) {
      console.error("Failed to revoke team invite link", err);
      showToast.error(t("team.inviteLink.toasts.revokeFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    await copyText(link.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: t("team.inviteLink.shareTitle", { team: team.name }),
        text: team.under16
          ? t("team.inviteLink.shareTextGuardian", { team: team.name })
          : t("team.inviteLink.shareTextPlayer", { team: team.name }),
        url: link.url,
      });
    } catch {
      // Dismissing the share sheet rejects; nothing to do.
    }
  };

  const expired = link?.status === "expired";

  return (
    <Modal show={show} onClose={onClose} maxWidth="520px">
      <div className="flex items-start justify-between gap-4 border-b border-[#F3F4F6] px-7 pb-5 pt-6">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#FFF4ED] text-[#E07A3A]">
            <Link2 size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#111827]">
              {t("team.inviteLink.title")}
            </h2>
            <p className="mt-0.5 text-sm text-[#6B7280]">
              {team?.under16
                ? t("team.inviteLink.subtitleGuardian")
                : t("team.inviteLink.subtitlePlayer")}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label={t("common.actions.close")}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-[#6B7280] transition-colors duration-150 hover:bg-[#F3F4F6] hover:text-[#374151]"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-7 py-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="animate-spin text-[#E07A3A]" />
          </div>
        ) : !link ? (
          <div>
            <p className="text-[15px] leading-relaxed text-[#374151]">
              {team?.under16
                ? t("team.inviteLink.howItWorksGuardian")
                : t("team.inviteLink.howItWorksPlayer")}
            </p>
            <button
              onClick={create}
              disabled={busy}
              className={`${primaryButton} mt-6 w-full py-3 text-base`}
            >
              {busy ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Link2 size={18} />
              )}
              {t("team.inviteLink.create")}
            </button>
          </div>
        ) : expired ? (
          <div>
            <div className="flex gap-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4">
              <AlertTriangle
                size={18}
                className="mt-0.5 flex-shrink-0 text-[#B45309]"
              />
              <p className="text-sm leading-relaxed text-[#92400E]">
                {t("team.inviteLink.expiredOn", {
                  date: formatDate(link.expires_at),
                })}
              </p>
            </div>
            <button
              onClick={create}
              disabled={busy}
              className={`${primaryButton} mt-6 w-full py-3 text-base`}
            >
              {busy ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
              {t("team.inviteLink.createNew")}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={link.url}
                aria-label={t("team.inviteLink.title")}
                onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 rounded-[10px] border border-[#E5E7EB] bg-[#F9FAFB] px-3.5 py-3 font-mono text-[13px] text-[#111827] outline-none focus:border-[#E07A3A]"
              />
              <button onClick={handleCopy} className={primaryButton}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied
                  ? t("team.inviteLink.copied")
                  : t("team.inviteLink.copy")}
              </button>
            </div>
            {canShare && (
              <button
                onClick={handleShare}
                className={`${secondaryButton} mt-2 w-full`}
              >
                <Share2 size={16} />
                {t("team.inviteLink.share")}
              </button>
            )}
            <p className="mt-3 text-sm text-[#6B7280]">
              {t("team.inviteLink.expiresOn", {
                date: formatDate(link.expires_at),
              })}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-[#374151]">
              {team?.under16
                ? t("team.inviteLink.howItWorksGuardian")
                : t("team.inviteLink.howItWorksPlayer")}
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#F3F4F6] pt-5">
              <button
                onClick={() => setConfirm("regenerate")}
                disabled={busy}
                className={secondaryButton}
              >
                <RefreshCw size={16} />
                {t("team.inviteLink.regenerate")}
              </button>
              <button
                onClick={() => setConfirm("revoke")}
                disabled={busy}
                className="rounded-[10px] px-3 py-2.5 text-sm font-medium text-[#DC2626] transition-colors duration-150 hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("team.inviteLink.revoke")}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirm === "regenerate"}
        title={t("team.inviteLink.confirmRegenerate.title")}
        body={t("team.inviteLink.confirmRegenerate.body")}
        confirmLabel={t("team.inviteLink.confirmRegenerate.confirm")}
        onConfirm={create}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "revoke"}
        title={t("team.inviteLink.confirmRevoke.title")}
        body={t("team.inviteLink.confirmRevoke.body")}
        confirmLabel={t("team.inviteLink.confirmRevoke.confirm")}
        danger
        onConfirm={revoke}
        onCancel={() => setConfirm(null)}
      />
    </Modal>
  );
};

export default TeamInviteLinkModal;
